// 定义 Web Component Dialog 组件
// 考虑到这边的业务弹窗不像传统的toB 会有固定的title footer（按钮区域）
// 这边的组件设计成更通用的形式，支持通过 slot 自定义内容和按钮
class CustomDialog extends HTMLElement {
  constructor() {
    super();
    // 创建 Shadow DOM（隔离组件样式和 DOM）
    this.attachShadow({ mode: "open" });
    // 组件配置：从自定义属性读取初始值
    this.config = {
      hideMask: this.hasAttribute("hide-mask"), // 是否隐藏遮罩层
      isOpen: false, // 初始状态：关闭
      width: this.getAttribute("width") || "520px", // 弹窗宽度
      height: this.getAttribute("height") || "auto", // 弹窗高度
      padding: this.getAttribute("padding") || "24px", // 内容区内边距
      mobileCss: this.getAttribute("mobile-css") || "",
      //是否可以点击遮罩关闭,默认值为true
      isClickMaskHidden: this.getAttribute("mask-close") || "true",
      //抛出事件的前缀
      eventPrefix: this.getAttribute("event-prefix") || "",
    };

    this.render();

    this.dialogEl = this.shadowRoot.querySelector(".dialog");
    this.maskEl = this.shadowRoot.querySelector(".dialog-mask");
    this.closeBtn = this.shadowRoot.querySelector(".dialog-close");

    // 绑定事件处理函数（确保 this 指向组件实例）
    this.handleClose = this.handleClose.bind(this);
    this.handleMaskClick = this.handleMaskClick.bind(this);
  }

  // 组件渲染：生成 DOM 结构和样式
  render() {
    // 组件样式（使用 Shadow DOM 隔离，不影响外部）
    const style = `
            <style>
                :host {
                    pointer-events: none;
                }
                :host(.open) {
                    pointer-events: auto;
                }
                /* 遮罩层样式 */
                .dialog-mask {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background-color: rgba(0, 0, 0, 0.5);
                    display: none;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                    z-index: 1000;
                }

                /* 对话框容器样式 */
                .dialog {
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    background-color: white;
                    border-radius: 8px;
                    width: ${this.config.width};
                    overflow: hidden;
                    max-height: ${this.config.height};
                    display: flex;
                    flex-direction: column;
                    opacity: 0;
                    transition: all 0.3s ease;
                    z-index: 1001;
                }

                .dialog-close {
                    position: absolute;
                    top: 16px;
                    right: 16px;
                    color: #63666A;
                    cursor: pointer;
                    z-index:1
                }

                /* 内容区域样式 */
                .dialog-content {
                    padding: ${this.config.padding};
                    overflow-y: auto;
                    flex: 1;
                    min-height: 0;
                }

                /* 打开状态样式 */
                .dialog.open {
                     display: flex;
                    opacity: 1;
                    transform: translate(-50%, -50%) scale(1);
                }
                .dialog-mask.open {
                    display: block;
                    opacity: 1;
                }

                /* 隐藏遮罩的样式 */
                .dialog-mask.hidden {
                    display: none !important;
                }
                /* 移动端样式 */
                @media screen and (max-width: 768px) {
                    .dialog-close {
                        width: 6.4vw;
                        height: 6.4vw;
                    }
                    ${this.config.mobileCss}
                }
            </style>
        `;

    // 组件 DOM 结构（使用插槽 Slot 支持内容自定义）
    const template = `
            ${style}
            <!-- 遮罩层 -->
            <div class="dialog-mask ${
              this.config.hideMask ? "hidden" : ""
            }"></div>
            
            <!-- 对话框主体 -->
            <div class="dialog">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" class="dialog-close" preserveAspectRatio="xMidYMid meet">
                    <rect width="2.33551" height="23.1195" transform="matrix(0.70712 -0.707094 0.70712 0.707094 11.0034 12.6562)" fill="#63666A"/>
                    <rect width="2.33551" height="23.1195" transform="matrix(0.70712 0.707094 -0.70712 0.707094 27.3486 11)" fill="#63666A"/>
                </svg>
                
                <!-- 内容插槽（默认内容区域，外部可插入任意 HTML)-->
                <div class="dialog-content">
                    <slot></slot> <!-- 外部内容会插入到这里 -->
                </div>
            </div>
        `;

    // 将模板插入 Shadow DOM
    this.shadowRoot.innerHTML = template;
  }

  // 组件挂载到 DOM 后执行
  connectedCallback() {
    //判断当前是否是手机端
    const isMobile = window.innerWidth <= 768;
    this.closeBtn.addEventListener(
      isMobile ? "touchstart" : "click",
      this.handleClose
    );
    if (this.config.isClickMaskHidden === "true") {
      this.maskEl.addEventListener(isMobile ? "touchstart" : "click", this.handleMaskClick);
    }
  }

  // 生命周期：组件从 DOM 移除前执行（清理资源）
  disconnectedCallback() {
    const isMobile = window.innerWidth <= 768;
    // 移除事件监听（避免内存泄漏）
    this.closeBtn.removeEventListener(isMobile ? "touchstart" : "click", this.handleClose);
    if (this.config.isClickMaskHidden === "true") {
      this.maskEl.removeEventListener(isMobile ? "touchstart" : "click", this.handleMaskClick);
    }
  }

  // 打开对话框
  open() {
    if (this.config.isOpen) return; // 避免重复打开
    this.config.isOpen = true;
    this.classList.add('open'); 
    this.dialogEl.classList.add("open");
    this.maskEl.classList.add("open");
    const eventName = "dialog-open";
    document.body.style.overflow = 'hidden';
    // 触发自定义事件（通知外部：对话框已打开）
    this.dispatchEvent(new CustomEvent(this.config.eventPrefix + eventName));
  }

  // 关闭对话框
  close() {
    if (!this.config.isOpen) return; // 避免重复关闭
    this.config.isOpen = false;
    this.classList.remove('open'); // 从宿主元素移除，禁用 pointer-events
    this.dialogEl.classList.remove("open");
    this.maskEl.classList.remove("open");
    const eventName = "dialog-close";
    document.body.style.overflow = '';
    // 触发自定义事件（通知外部：对话框已关闭）
    this.dispatchEvent(new CustomEvent(this.config.eventPrefix + eventName));
  }

  // 点击关闭按钮
  handleClose() {
    this.close();
  }

  // 点击遮罩层（关闭对话框）
  handleMaskClick(e) {
    // 仅当点击遮罩层本身（不是对话框内容）时触发
    if (e.target === this.maskEl && !this.config.hideMask) {
      this.close();
    }
  }
}

customElements.define("custom-dialog", CustomDialog);
